// API Service for Zibit Backend
// Configure your backend base URL here
const API_BASE_URL =
  process.env.REACT_APP_API_URL || '/api';

/**
 * Login user with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} - Login response with authToken and user data
 */
export async function loginUser(email, password) {
  try {
    const response = await fetch(`${API_BASE_URL}/user/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        password: password,
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid credentials.');
      } else if (response.status === 400) {
        throw new Error('Invalid request.');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }

    const data = await response.json();
    return {
      success: true,
      token: data.authToken,
      user: {
        userId: data.userId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        name: `${data.firstName} ${data.lastName}`,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} - Registration response
 */
export async function registerUser(userData) {
  try {
    const response = await fetch(`${API_BASE_URL}/user/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        birthDate: userData.birthDate,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Registration failed.');
    }

    const data = await response.json();
    return {
      success: true,
      userId: data.userId,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Verify user account with verification code
 * @param {string} userId - User ID
 * @param {string} verificationCode - 6-digit verification code
 * @returns {Promise<Object>} - Verification response
 */
export async function verifyUser(userId, verificationCode) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/user/register?id=${userId}&verificationCode=${verificationCode}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Verification failed.');
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Request password reset
 * @param {string} email - User's email
 * @returns {Promise<Object>} - Reset request response
 */
export async function requestPasswordReset(email) {
  try {
    const response = await fetch(`${API_BASE_URL}/user/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
      }),
    });

    if (!response.ok) {
      throw new Error('Password reset request failed.');
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Change password with reset code
 * @param {string} userId - User ID
 * @param {string} code - Reset code
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - Password change response
 */
export async function changePassword(userId, code, newPassword) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/user/change?id=${userId}&code=${code}&password=${newPassword}`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      throw new Error('Password change failed.');
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Fetch dashboard summary stats for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Dashboard stats
 */
export async function fetchDashboard(userId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/dashboard?userId=${userId}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch dashboard data.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Fetch events for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - List of events
 */
export async function fetchEvents(userId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/events?userId=${userId}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch events.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Create a new event
 * @param {Object} eventData - Event data
 * @returns {Promise<Object>} - Created event
 */
export async function createEvent(eventData) {
  try {
    const response = await fetch(`${API_BASE_URL}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      throw new Error('Failed to create event.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
export async function deleteEvent(eventId) {
  try {
    const response = await fetch(`${API_BASE_URL}/events?id=${eventId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete event.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
export async function updateEvent(eventId, eventData) {
  try {
    const response = await fetch(`${API_BASE_URL}/events?id=${eventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      throw new Error('Failed to update event.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
/**
 * Fetch leads for a user, optionally filtered by event
 * @param {string} userId - User ID
 * @param {string} [eventId] - Optional event ID to filter by
 * @returns {Promise<Object>} - List of leads
 */
export async function fetchLeads(userId, eventId) {
  try {
    let url = `${API_BASE_URL}/leads?userId=${userId}`;
    if (eventId) url += `&eventId=${eventId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch leads.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Create a new lead
 * @param {Object} leadData - Lead data
 * @returns {Promise<Object>} - Created lead
 */
export async function createLead(leadData) {
  try {
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(leadData),
    });

    if (!response.ok) {
      throw new Error('Failed to create lead.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Fetch tasks for a user, optionally filtered by event
 * @param {string} userId - User ID
 * @param {string} [eventId] - Optional event ID to filter by
 * @returns {Promise<Object>} - List of tasks
 */
export async function fetchTasks(userId, eventId) {
  try {
    let url = `${API_BASE_URL}/tasks?userId=${userId}`;
    if (eventId) url += `&eventId=${eventId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch tasks.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Create a new task
 * @param {Object} taskData - Task data
 * @returns {Promise<Object>} - Created task
 */
export async function createTask(taskData) {
  try {
    const response = await fetch(`${API_BASE_URL}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(taskData),
    });

    if (!response.ok) {
      throw new Error('Failed to create task.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
export async function deleteTask(taskId) {
  try {
    const response = await fetch(`${API_BASE_URL}/tasks?id=${taskId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete task.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
export async function updateTask(taskId, taskData) {
  try {
    const response = await fetch(`${API_BASE_URL}/tasks?id=${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(taskData),
    });

    if (!response.ok) {
      throw new Error('Failed to update task.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
/**
 * Fetch sales for a user, optionally filtered by event
 * @param {string} userId - User ID
 * @param {string} [eventId] - Optional event ID to filter by
 * @returns {Promise<Object>} - List of sales
 */
export async function fetchSales(userId, eventId) {
  try {
    let url = `${API_BASE_URL}/sales?userId=${userId}`;
    if (eventId) url += `&eventId=${eventId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch sales.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Create a new sale
 * @param {Object} saleData - Sale data
 * @returns {Promise<Object>} - Created sale
 */
export async function createSale(saleData) {
  try {
    const response = await fetch(`${API_BASE_URL}/sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(saleData),
    });

    if (!response.ok) {
      throw new Error('Failed to create sale.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Fetch budget items for a user, optionally filtered by event
 * @param {string} userId - User ID
 * @param {string} [eventId] - Optional event ID to filter by
 * @returns {Promise<Object>} - List of budget items
 */
export async function fetchBudget(userId, eventId) {
  try {
    let url = `${API_BASE_URL}/budget?userId=${userId}`;
    if (eventId) url += `&eventId=${eventId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch budget.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Create a new budget item
 * @param {Object} budgetData - Budget item data
 * @returns {Promise<Object>} - Created budget item
 */
export async function createBudgetItem(budgetData) {
  try {
    const response = await fetch(`${API_BASE_URL}/budget`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(budgetData),
    });

    if (!response.ok) {
      throw new Error('Failed to create budget item.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
/**
 * Search trade shows from Solr index
 * @param {string} query - Search term
 * @returns {Promise<Object>} - List of matching trade shows
 */
export async function searchTradeShows(query) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/tradeshows/search?q=${encodeURIComponent(query)}`
    );

    if (!response.ok) {
      throw new Error('Failed to search trade shows.');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}


export async function fetchEventOutcomes(eventId) {
  try {
    const response = await fetch(`${API_BASE_URL}/event-outcomes?eventId=${eventId}`);
    if (!response.ok) throw new Error('Failed to fetch outcomes');
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function fetchShowReview(eventId) {
  try {
    const response = await fetch(`${API_BASE_URL}/show-review?eventId=${eventId}`);
    if (!response.ok) throw new Error('Failed to fetch review');
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function saveShowReview(reviewData) {
  try {
    const response = await fetch(`${API_BASE_URL}/show-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reviewData),
    });
    if (!response.ok) throw new Error('Failed to save review');
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function deleteSale(saleId) {
  try {
    const response = await fetch(`${API_BASE_URL}/sales?id=${saleId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete sale');
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export default {
  loginUser,
  registerUser,
  verifyUser,
  requestPasswordReset,
  changePassword,
  fetchDashboard,
  fetchEvents,
  createEvent,
  deleteEvent, 
  updateEvent, 
  fetchLeads,
  createLead,
  fetchTasks,
  createTask,
  deleteTask,
  updateTask,
  fetchSales,
  createSale,
  fetchBudget,
  createBudgetItem,
  searchTradeShows,
};
export async function fetchBudgetLines({ userId, eventId } = {}) {
  try {
    let url = `${API_BASE_URL}/budget-lines?`;
    if (eventId) url += `eventId=${eventId}`;
    else if (userId) url += `userId=${userId}`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    }
    return { success: false, error: 'Failed to fetch budget items.' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function createBudgetLine(itemData) {
  try {
    const response = await fetch(`${API_BASE_URL}/budget-lines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(itemData),
    });
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    }
    return { success: false, error: 'Failed to create budget item.' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function deleteBudgetLine(itemId) {
  try {
    const response = await fetch(`${API_BASE_URL}/budget-lines?id=${itemId}`, {
      method: 'DELETE',
    });
    if (response.ok) return { success: true };
    return { success: false, error: 'Failed to delete budget item.' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function sendBudgetEmail(eventId, recipients) {
  try {
    const response = await fetch(`${API_BASE_URL}/budget-lines?action=send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId, recipients }),
    });
    const data = await response.json();
    if (response.ok && data.success) {
      return { success: true, sent: data.sent };
    }
    return { success: false, error: data.failed ? data.failed.join('; ') : 'Failed to send.' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function sendForecastEmail(userId, recipients) {
  try {
    const response = await fetch(`${API_BASE_URL}/budget-lines?action=send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope: 'forecast', userId, recipients }),
    });
    const data = await response.json();
    if (response.ok && data.success) {
      return { success: true, sent: data.sent };
    }
    return { success: false, error: data.failed ? data.failed.join('; ') : 'Failed to send.' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function createEventShare({ eventId, userId, recipientEmail, recipientName, sections }) {
  try {
    const response = await fetch(`${API_BASE_URL}/shares`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId, userId, recipientEmail, recipientName, sections }),
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.message || 'Failed to create share.' };
    return { success: true, ...data };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function fetchEventShares(eventId) {
  try {
    const response = await fetch(`${API_BASE_URL}/shares?eventId=${encodeURIComponent(eventId)}`);
    if (!response.ok) return { success: false, error: 'Failed to load shares.' };
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function revokeEventShare(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/shares?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!response.ok) return { success: false, error: 'Failed to revoke share.' };
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function fetchSharedView(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/shared/${encodeURIComponent(token)}`);
    if (!response.ok) {
      return { success: false, error: 'This link is no longer available.' };
    }
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function createSupportTicket({ userId, title, description, type }) {
  try {
    const response = await fetch(`${API_BASE_URL}/support/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, title, description, type }),
    });
    if (!response.ok) {
      const text = await response.text();
      return { success: false, error: text || 'Could not submit your request.' };
    }
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function fetchMySupportTickets(userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/support/tickets?userId=${encodeURIComponent(userId)}`);
    if (!response.ok) return { success: false, error: 'Could not load your requests.' };
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function fetchTicketMessages(ticketId, userId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/ticket-messages?ticketId=${encodeURIComponent(ticketId)}&userId=${encodeURIComponent(userId)}`
    );
    if (!response.ok) return { success: false, error: 'Could not load this conversation.' };
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function replyToTicket(ticketId, userId, message) {
  try {
    const response = await fetch(`${API_BASE_URL}/ticket-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticketId, userId, message }),
    });
    if (!response.ok) return { success: false, error: 'Could not send your reply.' };
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: err.message };
  }
}
